/**
 * Scoring algorithms for RhythmSense.
 *
 * Timing windows:
 *   PERFECT  ±50 ms  → 100 points, accuracy 1.0
 *   GOOD     ±100 ms → 50 points,  accuracy 0.5
 *   MISS     anything outside → 0 points
 */

import { HitScore } from '../types';

/**
 * Calculate the score for a single note hit.
 *
 * @param actualTime - The time (ms) when the note was supposed to be hit
 * @param hitTime    - The time (ms) when the player actually pressed the key
 * @param bpm        - Current track BPM (used for tolerance ratio)
 * @returns HitScore containing rating, points, and accuracy
 */
export function calculateScore(
  actualTime: number,
  hitTime: number,
  bpm: number
): HitScore {
  const diffMs = Math.abs(actualTime - hitTime);
  const beatDuration = 60_000 / bpm;
  // toleranceRatio is informational – not used for window decisions per spec
  const _toleranceRatio = diffMs / beatDuration;
  void _toleranceRatio;

  if (diffMs <= 50) {
    return { rating: 'PERFECT', points: 100, accuracy: 1.0 };
  }
  if (diffMs <= 100) {
    return { rating: 'GOOD', points: 50, accuracy: 0.5 };
  }
  return { rating: 'MISS', points: 0, accuracy: 0 };
}

/**
 * Compute overall accuracy as a percentage from hit counts.
 *
 * @param perfectCount - Number of PERFECT hits
 * @param goodCount    - Number of GOOD hits
 * @param missCount    - Number of MISS hits
 * @returns Accuracy 0–100 (percentage)
 */
export function computeOverallAccuracy(
  perfectCount: number,
  goodCount: number,
  missCount: number
): number {
  const total = perfectCount + goodCount + missCount;
  if (total === 0) return 0;
  const weightedSum = perfectCount * 1.0 + goodCount * 0.5 + missCount * 0;
  return Number(((weightedSum / total) * 100).toFixed(2));
}

/**
 * Convert raw accuracy (0–1) to a letter grade.
 */
export function accuracyToGrade(accuracy: number): string {
  if (accuracy >= 100) return 'S';
  if (accuracy >= 95)  return 'A+';
  if (accuracy >= 90)  return 'A';
  if (accuracy >= 80)  return 'B';
  if (accuracy >= 70)  return 'C';
  if (accuracy >= 50)  return 'D';
  return 'F';
}

/**
 * Compute combo multiplier.  Above combo threshold the score per note
 * is multiplied.
 */
export function comboMultiplier(combo: number): number {
  if (combo >= 100) return 4;
  if (combo >= 50)  return 3;
  if (combo >= 20)  return 2;
  return 1;
}

/**
 * Validate that a hit time is within the miss window (200 ms either side).
 * Used to filter out phantom key presses that are nowhere near any note.
 */
export const MAX_HIT_WINDOW_MS = 200;
